import { BoxProps } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  IAutocompleteItem,
} from 'ui-components/dist/Autocomplete';
import { useDebounce } from '~/hooks/api/useDebounce';
import { useLocationSearch } from '~/screens/MyAgency/hooks/useLocationSearch';

import { mapLocationToAutocompleteItem } from './mappers';
import { ILocation } from './types';

export interface ISearchLocationLegacyProps extends BoxProps {
  values: ILocation;
  setValues: (values: ILocation | null) => void;
  trackId?: string;
  inputProps?: {
    clearButtonProps?: {
      'aria-label': string;
      'track-id': string;
    };
  };
}

export const SearchLocationLegacy: React.FC<ISearchLocationLegacyProps> = ({
  values,
  setValues,
  trackId,
  inputProps: _,
}) => {
  const [value, setValue] = useState<string | null>(null);
  const debouncedValue = useDebounce(value, 300);
  const selectedOption = mapLocationToAutocompleteItem(values);

  const { data } = useLocationSearch(debouncedValue);

  const options: IAutocompleteItem[] = useMemo(() => {
    if (data?.results) {
      return data?.results
        .map(mapLocationToAutocompleteItem)
        .filter((item: IAutocompleteItem) => item);
    }
    return [];
  }, [data]);

  const handleSearch = useCallback(
    (updatedValue: string) => {
      setValue(updatedValue ? updatedValue : null);
      if (!updatedValue.length) setValues(null);
    },
    [setValue, setValues],
  );

  const handleSelectOption = useCallback(
    ({ id, label }: IAutocompleteItem) => {
      if (id) {
        const [city, country] = label.split(', ');
        setValues({
          id,
          city,
          country,
        });
      }
    },
    [setValues],
  );

  return (
    <Autocomplete
      ariaLabel="Select City, Country"
      onChange={handleSearch}
      onClear={() => setValues(null)}
      onSelectOption={handleSelectOption}
      options={options}
      placeholder="City, Country"
      selectedOption={selectedOption ?? undefined}
      size="lg"
      trackId={trackId}
    />
  );
};
